import { observer } from 'mobx-react-lite';
import type { ChangeEvent } from 'react';
import Query from '../../store/utils/query/Query';
import QueryResponse from '../../store/utils/query/QueryResponse';
import { IconButton } from '../controls';
import { SelectField } from '../form';
import classes from './Pagination.module.scss';

const PAGE_SIZES = [10, 20, 50, 100, 200, 500, 1000];

const ROWS_PER_PAGE = PAGE_SIZES.map((size) => ({ label: size, value: size }));

export default observer(function Pagination({
  query,
  rowsText = 'Rows per page',
}: {
  query: Query<any, any>;
  rowsText?: string;
}) {
  const { response } = query;

  if (!query.promise || !response) {
    return null;
  }

  return (
    <div className={classes.root}>
      <RowsPerPage response={response} title={rowsText} />
      <OffsetInfo response={response} />
      <div>
        <IconButton
          disabled={!response.hasPrevPage}
          icon="chevron-left"
          onClick={response.toPreviousPage}
          size="small"
        />
        <IconButton
          disabled={!response.hasNextPage}
          icon="chevron-right"
          onClick={response.toNextPage}
          size="small"
        />
      </div>
    </div>
  );
});

const RowsPerPage = observer(function RowsPerPage({
  response,
  title = 'Rows per page',
}: {
  response: QueryResponse<any>;
  title?: string;
}) {
  async function onChange(event: ChangeEvent<HTMLInputElement>) {
    await response.loadWithLimit(Number.parseInt(event.target.value, 10) || 20);
  }

  return (
    <div className={classes.rowsPerPage}>
      {title}:
      <SelectField
        className={classes.select}
        InputProps={{
          classes: {
            root: classes.selectInputRoot,
            input: classes.selectInput,
          },
          disableUnderline: true,
        }}
        onChange={onChange}
        options={ROWS_PER_PAGE}
        sx={{ mt: 0, width: 'auto' }}
        value={response.limit}
      />
    </div>
  );
});

const OffsetInfo = observer(function OffsetInfo({
  response,
}: {
  response: QueryResponse<any>;
}) {
  const { itemCount, limit, offset, totalCount } = response;

  if (totalCount === -1) {
    return null;
  }

  const from = itemCount > 0 ? offset + 1 : 0;
  const to = Math.min(itemCount > 0 ? offset + limit : 0, totalCount);

  return (
    <div className={classes.offsetInfo}>
      {from}-{to} of {totalCount}
    </div>
  );
});
